import { Button, cn, Modal } from "@etudescarre/ui";
import { ClockCountdown as ClockCountdownIcon } from "@phosphor-icons/react";
import { formatDay } from "@/lib/utils/date";
import { VISA_EN_RETARD_LABEL } from "./synthese-cell";

export type SyntheseAvisData = {
  documentName: string;
  /** Un avis requis est en attente avec délai dépassé. */
  visaEnRetard: boolean;
  syntheseAvis: "obs" | "ok" | "attente" | null;
  notices: Array<{
    companyName: string;
    intervenantConcerne: string;
    avisRequis: boolean;
    bordereauNumero: string | null;
    bordereauDate: string | null;
    // null = aucun avis consigné pour ce destinataire.
    statusAvis: string | null;
    observations: string | null;
    rapportNumero: string | null;
    rapportDate: string | null;
  }>;
};

export type SyntheseAvisPopupProps = {
  open: boolean;
  onClose: () => void;
  data: SyntheseAvisData | null;
};

const statusLabels: Record<string, string> = {
  favorable: "Favorable",
  defavorable: "Defavorable",
  vu_avec_observation: "Vu avec obs.",
  vu_sans_observation: "Vu sans obs.",
};

const statusColors: Record<string, string> = {
  favorable: "text-green-600",
  defavorable: "text-red-600",
  vu_avec_observation: "text-amber-600",
  vu_sans_observation: "text-green-600",
};

const syntheseLabels: Record<string, string> = {
  obs: "Observations",
  ok: "Conforme",
  attente: "En attente",
};

export function SyntheseAvisPopup({
  open,
  onClose,
  data,
}: SyntheseAvisPopupProps) {
  if (!data) return null;

  return (
    <Modal.Root open={open} onOpenChange={(e) => !e.open && onClose()}>
      <Modal.Backdrop />
      <Modal.Positioner>
        <Modal.Content size="2xl">
          <Modal.Header>
            <Modal.Title>Synthese des avis</Modal.Title>
          </Modal.Header>

          <div className="px-6 pb-6">
            <div className="mb-4">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                {data.documentName}
              </p>
              <span className="mt-1 inline-flex items-center gap-1.5">
                {data.syntheseAvis && (
                  <span className="inline-block rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium dark:bg-zinc-800">
                    {syntheseLabels[data.syntheseAvis] ?? data.syntheseAvis}
                  </span>
                )}
                {/* Même picto "visa en retard" que sur la liste */}
                {data.visaEnRetard && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                    <ClockCountdownIcon size={16} weight="fill" />
                    {VISA_EN_RETARD_LABEL}
                  </span>
                )}
              </span>
            </div>

            {data.notices.length === 0 ? (
              <p className="text-sm text-zinc-500">Aucun avis disponible.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-zinc-200 dark:border-zinc-700">
                      <th className="px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                        Societe
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                        Intervenant
                      </th>
                      <th className="px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                        Requis
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                        Bord. N°
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                        Date
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                        Avis
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                        Observations
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.notices.map((notice, i) => (
                      <tr
                        key={`${notice.companyName}-${i}`}
                        className="border-b border-zinc-100 dark:border-zinc-700/50"
                      >
                        <td className="px-3 py-2 text-zinc-800 dark:text-zinc-200">
                          {notice.companyName}
                        </td>
                        <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400">
                          {notice.intervenantConcerne}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {notice.avisRequis ? (
                            <span className="text-blue-600">Oui</span>
                          ) : (
                            <span className="text-zinc-400">Non</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400">
                          {notice.bordereauNumero ?? "-"}
                        </td>
                        <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400 whitespace-nowrap">
                          {notice.bordereauDate
                            ? formatDay(notice.bordereauDate)
                            : "-"}
                        </td>
                        <td
                          className={cn(
                            "px-3 py-2 font-medium",
                            (notice.statusAvis
                              ? statusColors[notice.statusAvis]
                              : undefined) ?? "text-zinc-600",
                          )}
                        >
                          {notice.statusAvis
                            ? (statusLabels[notice.statusAvis] ??
                              notice.statusAvis)
                            : "-"}
                        </td>
                        <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400 max-w-[200px] truncate">
                          {notice.observations ?? "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <Modal.Footer>
            <Button onClick={onClose}>Fermer</Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Positioner>
    </Modal.Root>
  );
}
